/**
 * The pilot brain that asks a model. It answers the sim's requests through the voice service and
 * hands the decision back with what it cost; if it is slow, refused, over budget or offline, the
 * sim speaks its own words at the deadline and the session carries on.
 */
import type { AsyncPilotBrain, BrainRequest, Sim } from '@atc/sim';
import type { CostMeter, ModelUsage } from '../costs';

export interface LlmBrainOptions {
  sim: Sim;
  provider: 'auto' | 'local' | 'hosted';
  meter: CostMeter;
  /** Stop asking once the estimate passes this, and say why once. */
  capUsd?: number;
  onCapReached?(totalUsd: number): void;
  onError?(message: string): void;
  maxConcurrent?: number;
  fetchFn?: typeof fetch;
}

export interface LlmBrain extends AsyncPilotBrain {
  /** Requests that were never sent: too many at once, or the budget was spent. */
  readonly skipped: number;
  destroy(): void;
}

import { attribution } from '../attribution';

const ENDPOINT = '/api/voice/brain';

export function createLlmBrain(o: LlmBrainOptions): LlmBrain {
  const fetchFn = o.fetchFn ?? fetch;
  const maxConcurrent = o.maxConcurrent ?? 2;
  const inFlight = new Map<number, AbortController>();
  let skipped = 0, capped = false, destroyed = false;

  async function ask(req: BrainRequest): Promise<void> {
    const controller = new AbortController();
    inFlight.set(req.id, controller);
    try {
      const res = await fetchFn(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-atc-voice': '1', ...attribution() },
        signal: controller.signal,
        body: JSON.stringify({ provider: o.provider, trigger: req.trigger, situation: req.situation }),
      });
      if (!res.ok) {
        // The sim will speak its own words at the deadline; say why once per kind of failure.
        o.onError?.(res.status === 429 ? 'Pilot brain is rate limited; the rules brain is flying.' : `Pilot brain unavailable (${res.status}).`);
        return;
      }
      const usage = readUsage(res.headers.get('x-atc-usage'));
      o.meter.record('brain', usage);
      const decision = await res.json();
      if (!destroyed) o.sim.deliverDecision(req.id, decision, usage as Record<string, number>);
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') o.onError?.('Pilot brain could not be reached; the rules brain is flying.');
    } finally {
      inFlight.delete(req.id);
    }
  }

  return {
    id: `llm:${o.provider}`,
    mode: 'async',
    get skipped() { return skipped; },
    request(req) {
      if (destroyed) return;
      if (o.capUsd !== undefined && o.meter.summary(1).totalUsd >= o.capUsd) {
        skipped++;
        if (!capped) { capped = true; o.onCapReached?.(o.meter.summary(1).totalUsd); }
        return;
      }
      // Never queue: an answer that arrives after its deadline is wasted anyway.
      if (inFlight.size >= maxConcurrent) { skipped++; return; }
      void ask(req);
    },
    cancel(id) { inFlight.get(id)?.abort(); inFlight.delete(id); },
    destroy() { destroyed = true; for (const c of inFlight.values()) c.abort(); inFlight.clear(); },
  };
}

function readUsage(header: string | null): ModelUsage {
  if (!header) return {};
  try {
    const u = JSON.parse(header);
    return u && typeof u === 'object' ? u as ModelUsage : {};
  } catch {
    return {};
  }
}
